export default class Util {
  static OFFICE_SERVER_URL = 'https://gitlab.com/'

  /**
  * @description Returns an include statement that holds the user's main Organisation
  **/
  static getOrganizationInclude (models, user, host = 'localhost',
    exclude = ['stripeSecretKey', 'mandrillKey']) {
    return new Promise((resolve, reject) => {
      this.getOrganization(models, user, host, exclude).then(function (organization) {
        let include = {
          model: models.Organization,
          include: { model: models.AmazonImage, as: 'OrganizationLogo' },
          where: { uuid: organization.uuid },
          attributes: {
            exclude
          }
        }
        resolve(include)
      })
    })
  }

  /**
  * @description Returns an include statement that holds all of user's Organisations
  **/
  static getOrganizationsInclude (models, user, host = 'localhost',
    exclude = ['stripeSecretKey', 'mandrillKey']) {
    return new Promise((resolve, reject) => {
      this.getOrganizations(models, user, host, exclude).then(function (organizations) {
        let $in = organizations.map(function (org) { return org.uuid })
        let include = {
          model: models.Organization,
          include: { model: models.AmazonImage, as: 'OrganizationLogo' },
          where: { uuid: $in },
          attributes: {
            exclude
          }
        }
        resolve(include)
      })
    })
  }

  /**
  * @description Returns the main OrganisationUuid of the user
  **/
  static getOrganizationUuid (models, user, host = 'localhost',
    exclude = ['stripeSecretKey', 'mandrillKey']) {
    return new Promise((resolve, reject) => {
      this.getOrganization(models, user, host, exclude).then(function (organization) {
        resolve(organization.uuid)
      })
    })
  }

  /**
  * @description Returns the main Organisation of the user
  **/
  static getOrganization (models, user, host = 'localhost',
    exclude = ['stripeSecretKey', 'mandrillKey']) {
    return new Promise((resolve, reject) => {
      this.getOrganizations(models, user, host, exclude).then(function (organizations) {
        let mainOrg = false
        for (let organization of organizations) {
          if (organization.Members && organization.Members[0].MemberContract && organization.Members[0].MemberContract.mainContract) {
            mainOrg = organization
          }
        }
        if (!mainOrg) mainOrg = organizations[0]
        resolve(mainOrg)
      })
    })
  }

  /**
  * @description Returns all Organisations of the user
  **/
  static getOrganizations (models, user, host = 'localhost',
    exclude = ['stripeSecretKey', 'mandrillKey']) {
    if (user && user.uuid) {
      return models.Organization.findAll({
        include: [
          {
            model: models.Account,
            through: {
              model: models.MemberContract
            },
            where: {
              uuid: user.uuid
            },
            as: 'Members'
          },
          { model: models.AmazonImage, as: 'OrganizationLogo' }
        ],
        attributes: {
          exclude
        }
      })
    } else {
      return models.Organization.findAll({
        where: {
          name: { $contains: [host] }
        },
        include: { model: models.AmazonImage, as: 'OrganizationLogo' },
        attributes: {
          exclude
        }
      })
    }
  }

  /**
  * @description Returns a single field, which will be either a hidden input or an object select input depending on the amount of organizations
  **/
  static buildOrganizationField (options) {
    let defaultOptions = {
      organizations: [],
      value: '',
      required: false
    }
    let opts = { ...defaultOptions, ...options }
    let field = {
      name: 'OrganizationUuid',
      type: 'object-select',
      label: 'Organisation',
      value: opts.value,
      objects: opts.organizations,
      objectName: 'pageTitle',
      required: opts.required
    }
    if (opts.organizations.length === 1) {
      field = {
        name: 'OrganizationUuid',
        type: 'hidden',
        label: 'Organisation',
        value: opts.organizations[0].uuid,
        required: opts.required
      }
    }
    return field
  }

  /**
  * @description Returns an array of 1 or 2 field(s), which will be either a hidden input or an object select input depending on the amount of organizations (intended to be merged into form fields)
  **/
  static buildContractField (options) {
    let defaultOptions = {
      organizations: [],
      values: [],
      value: '',
      required: false
    }
    let opts = { ...defaultOptions, ...options }
    let fields = [
      {
        name: 'MemberContracts',
        type: 'objects-select',
        label: 'Organisations',
        value: opts.values,
        objects: opts.organizations,
        objectName: 'pageTitle',
        required: opts.required
      },
      {
        name: 'mainContract',
        type: 'object-select',
        label: 'Main Organisation',
        objectName: 'pageTitle',
        objects: opts.organizations,
        required: opts.required,
        value: opts.value
      }
    ]
    if (opts.organizations.length === 1) {
      fields = [
        {
          name: 'MemberContracts',
          type: 'hidden',
          label: 'Organisations',
          value: [opts.organizations[0].uuid],
          required: opts.required
        },
        {
          name: 'mainContract',
          type: 'hidden',
          label: 'Main Organisation',
          required: opts.required,
          value: opts.organizations[0].uuid
        }
      ]
    }
    return fields
  }

  static round (num) {
    return Number(Math.round(num + 'e2') + 'e-2')
  }

  static calculateCosts (options) {
    let moment = require('moment-timezone')
    // constants
    const FRIDAY = 5
    const LAST_FRIDAY = -2
    // default options
    let defaultOptions = {
      Account: false,
      Organization: false,
      Car: {},
      Rate: {},
      Reservation: false,
      Lot: false,
      startDate: '',
      endDate: '',
      extras: [],
      useCredits: true,
      percentDiscount: false
    }
    // priceBreakdown for output
    let priceBreakdown = {
      days: 0,
      hours: 0,
      weekendDays: 0,
      overnightHours: 0,
      hourlySubTotal: 0,
      dailySubTotal: 0,
      weekendSubTotal: 0,
      overnightSubTotal: 0,
      extras: 0,
      subTotalCost: 0,
      totalCost: 0,
      originalCredit: 0,
      remainingCredit: 0,
      alreadyPaid: 0,
      creditUsed: 0,
      premiumAmount: 0,
      premiumReason: '',
      preDiscountCost: 0
    }
    // merge options
    let opts = { ...defaultOptions, ...options }
    // Set default discount rate times
    let overnightRateStart = { hours: 17, minutes: 0 }
    let overnightRateEnd = { hours: 8, minutes: 0 }
    let weekendRateStart = { hours: 17, minutes: 0 }
    let weekendRateEnd = { hours: 8, minutes: 0 }
    // Use discount rate times from org
    if (opts.Organization) {
      weekendRateStart = {
        hours: moment(opts.Organization.weekendRateStart).hour(),
        minutes: moment(opts.Organization.weekendRateStart).minute()
      }
      weekendRateEnd = {
        hours: moment(opts.Organization.weekendRateEnd).hour(),
        minutes: moment(opts.Organization.weekendRateEnd).minute()
      }
      overnightRateStart = {
        hours: moment(opts.Organization.overnightRateStart).hour(),
        minutes: moment(opts.Organization.overnightRateStart).minute()
      }
      overnightRateEnd = {
        hours: moment(opts.Organization.overnightRateEnd).hour(),
        minutes: moment(opts.Organization.overnightRateEnd).minute()
      }
    }
    // merge and overwrite specific values
    // if we have a car, but no lot
    if (opts.Car.uuid && !opts.Lot.uuid) {
      opts.Lot = opts.Car.Lot
    }
    // if we have a Reservation and theres no rate passed
    if (opts.Reservation.uuid) {
      if (!opts.Rate.uuid) {
        // Rate, Lot, Extras pulled from Reservation
        opts.Rate = {
          hourlyRate: opts.Reservation.hourlyRate,
          dailyRate: opts.Reservation.dailyRate,
          overnightRate: opts.Reservation.overnightRate,
          weekendRate: opts.Reservation.weekendRate,
          includeKms: opts.Reservation.includeKms,
          perKmCost: opts.Reservation.perKmCost
        }
      }
      opts.Lot = opts.Reservation.Lot
      opts.extras = opts.Reservation.extras
      priceBreakdown.alreadyPaid = this.round(opts.Reservation.quotedCost)
    }
    // work out minimum booking length for weekend rate to get best rate
    let minWeekendLength = 27.5
    // avoid divide by zero error for rates that may not have a hourly rate (billing exempt)
    if (opts.Rate.hourlyRate > 0 && opts.Rate.weekendRate > 0) {
      let y = (opts.Rate.weekendRate - opts.Rate.dailyRate) / opts.Rate.hourlyRate
      minWeekendLength = 24 + y
      minWeekendLength = Util.round(minWeekendLength - (minWeekendLength % 0.25) + 0.25)
    }
    if (!opts.Account.uuid && opts.Reservation.uuid) {
      opts.Account = opts.Reservation.Account
    }
    priceBreakdown.originalCredit = this.round(opts.Account.credits) || 0
    if (!opts.Lot.uuid) opts.Lot = opts.Car.Lot
    opts.startDate = moment(opts.startDate).tz(opts.Lot.timezone)
    opts.endDate = moment(opts.endDate).tz(opts.Lot.timezone)
    // calculate priceBreakdown
    let hours = opts.endDate.diff(opts.startDate, 'hours', true)
    let totalBookingHours = hours
    let totalBookingDays = Math.ceil(hours / 24.00)
    // weekend hours calculator
    if (opts.Lot.hasWeekend) {
      let weekendFactor = Math.floor(totalBookingDays / 5)
      for (let i = 0; i <= weekendFactor; i++) {
        // calculate start of week and end of week
        let bookingWeekStart = opts.startDate.clone().add(i * 5, 'days')
        let bookingWeekEnd = bookingWeekStart.clone().add(5, 'days')
        // check if the week ends after the endDate of the reservation
        if (bookingWeekEnd.isAfter(opts.endDate)) bookingWeekEnd = opts.endDate
        // assign friday that weekend starts based on booking start time
        let day = FRIDAY
        if (bookingWeekStart.day() === 0) day = LAST_FRIDAY
        // calculate when weekend starts and ends
        let weekendStart = bookingWeekStart.clone().day(day).set({'hour': weekendRateStart.hours, 'minute': weekendRateStart.minutes})
        let weekendEnd = weekendStart.clone().day(8).set({'hour': weekendRateEnd.hours, 'minute': weekendRateEnd.minutes})
        let bookingStartsOnWeekend = bookingWeekStart.isBetween(weekendStart, weekendEnd, null, '[]')
        let bookingEndsOnWeekend = bookingWeekEnd.isBetween(weekendStart, weekendEnd, null, '[]')
        if (bookingStartsOnWeekend) {
          weekendStart = bookingWeekStart
        }
        if (bookingEndsOnWeekend) {
          weekendEnd = bookingWeekEnd
        }
        // check if booking is over the weekend
        let overWeekend =
          weekendStart.isBetween(bookingWeekStart, bookingWeekEnd, null, '[]') ||
          weekendEnd.isBetween(bookingWeekStart, bookingWeekEnd, null, '[]')
        let minEndDateForWeekend = weekendStart.clone().add(minWeekendLength, 'hours')
        if (overWeekend) {
          hours = bookingWeekEnd.diff(bookingWeekStart, 'hours', true)
          let lowerHours = 0
          let upperHours = 0
          if (bookingWeekStart.isSameOrBefore(weekendStart)) {
            lowerHours = Math.abs(bookingWeekStart.diff(weekendStart, 'hours', true))
          }
          if (bookingWeekEnd.isSameOrAfter(weekendEnd)) {
            upperHours = Math.abs(weekendEnd.diff(bookingWeekEnd, 'hours', true))
          }
          let totalWeekendHours = hours - upperHours - lowerHours
          if (totalWeekendHours >= minWeekendLength) {
            priceBreakdown.weekendDays++
            totalBookingHours -= totalWeekendHours
          } else if (i < weekendFactor && opts.endDate.isSameOrAfter(minEndDateForWeekend)) {
            totalBookingHours -= totalWeekendHours
          } else if (i === weekendFactor && opts.startDate.isSameOrBefore(minEndDateForWeekend) && bookingWeekStart.isSameOrAfter(minEndDateForWeekend)) {
            totalBookingHours -= totalWeekendHours
          }
        }
      }
    }
    priceBreakdown.weekendSubTotal = this.round(opts.Rate.weekendRate * priceBreakdown.weekendDays)
    // overnight rate calculator
    if (opts.Lot.hasOvernight) {
      let overnightStart = opts.endDate.clone().set({'hour': overnightRateStart.hours, 'minute': overnightRateStart.minutes, 'second': 0})
      if (overnightStart.isSameOrAfter(opts.endDate)) {
        overnightStart.subtract(1, 'days')
      }
      let overnightEnd = overnightStart.clone().add(1, 'days').set({'hour': overnightRateEnd.hours, 'minute': overnightRateEnd.minutes, 'second': 0})
      // overnight rate can only apply to one night
      let isOverNight =
        opts.startDate.isBetween(overnightStart, overnightEnd, null, '[]') &&
        opts.endDate.isBetween(overnightStart, overnightEnd, null, '[]')
      if (isOverNight && totalBookingHours >= 6) {
        priceBreakdown.overnightSubTotal = opts.Rate.overnightRate
        priceBreakdown.overnightHours++
        totalBookingHours = 0
      }
    }
    // calculate price breakdown with left over hours
    priceBreakdown.days = this.round(Math.floor(totalBookingHours / 24.00))
    priceBreakdown.hours = this.round(totalBookingHours - priceBreakdown.days * 24)
    priceBreakdown.dailySubTotal = this.round(opts.Rate.dailyRate * priceBreakdown.days)
    priceBreakdown.hourlySubTotal = this.round(opts.Rate.hourlyRate * priceBreakdown.hours)
    // if the hourly subtotal is more than the daily rate, apply the daily rate for the hours
    if ((priceBreakdown.hourlySubTotal) > this.round(opts.Rate.dailyRate)) {
      priceBreakdown.days++
      priceBreakdown.hours = 0
      priceBreakdown.dailySubTotal = this.round(opts.Rate.dailyRate * priceBreakdown.days)
      priceBreakdown.hourlySubTotal = this.round(opts.Rate.hourlyRate * priceBreakdown.hours)
    }
    // check for lot premium
    if (opts.Lot.premiumReason && opts.Lot.premiumAmount) {
      if (this.round(opts.Rate.dailyRate) === 0 && this.round(opts.Rate.hourlyRate) === 0) {
        // if the rate doesnt charge the user - don't charge lot premiums
        opts.Lot.premiumAmount = 0
      }
      let premiumIncluded = false
      for (let i = 0; i < opts.extras.length; i++) {
        if (opts.extras[i].uuid === 'lot-premium') {
          premiumIncluded = true
        }
      }
      if (!premiumIncluded) {
        opts.extras.push({
          uuid: 'lot-premium',
          description: opts.Lot.premiumReason,
          name: opts.Lot.premiumName,
          cost: this.round(opts.Lot.premiumAmount),
          rateType: 'fixed'
        })
      }
    }
    // calculate extras onto price
    if (opts.extras && opts.extras.length > 0) {
      for (let extra of opts.extras) {
        if (!extra.overnightRate) {
          extra.overnightRate = this.round(extra.cost)
        }
        if (extra.rateType === 'fixed') {
          priceBreakdown.extras += this.round(extra.cost)
        } else if (extra.rateType === 'rate') {
          priceBreakdown.extras +=
            this.round(priceBreakdown.weekendDays) * this.round(extra.weekendRate) +
            this.round(priceBreakdown.days) * this.round(extra.dailyRate) +
            this.round(priceBreakdown.hours) * this.round(extra.hourlyRate) +
            this.round(priceBreakdown.overnightHours) * this.round(extra.overnightRate)
        }
      }
    }
    // subtotal calculation
    priceBreakdown.preDiscountCost = this.round(
      priceBreakdown.dailySubTotal +
      priceBreakdown.hourlySubTotal +
      priceBreakdown.overnightSubTotal +
      priceBreakdown.weekendSubTotal +
      priceBreakdown.extras
    )
    if (opts.percentDiscount > 0) {
      let discountFactor = (100 - opts.percentDiscount) / 100
      priceBreakdown.subTotalCost = this.round(
        (priceBreakdown.dailySubTotal + priceBreakdown.hourlySubTotal) * discountFactor +
        priceBreakdown.overnightSubTotal +
        priceBreakdown.weekendSubTotal +
        priceBreakdown.extras
      )
    } else {
      priceBreakdown.subTotalCost = priceBreakdown.preDiscountCost
    }
    // calculate if there will be refunds on reservation
    let refundCharge = true
    if (opts.Reservation) {
      let now = opts.Reservation.modifiedAt ? moment(opts.Reservation.modifiedAt) : moment()
      let createdAt = moment(opts.Reservation.createdAt)
      if (opts.startDate.diff(now, 'hours') < 24) {
        // reservation begins in less than 24 hours
        if (now.diff(createdAt, 'hours') >= 1) {
          // reservation was also created more than an hour ago
          refundCharge = false
        }
      }
    }
    let useCredits = opts.useCredits
    let creditsAndPaid = this.round(opts.Account.credits + priceBreakdown.alreadyPaid)
    priceBreakdown.remainingCredit = opts.Account.credits || 0
    if (useCredits) {
      // check if the subtotal is more than the user has already paid + their credits
      if (priceBreakdown.subTotalCost >= creditsAndPaid) {
        // cost is more than the credits
        // update total cost to minus the credits and already paid amounts
        priceBreakdown.totalCost = this.round(priceBreakdown.subTotalCost - creditsAndPaid)
        // use all remaining credits
        priceBreakdown.remainingCredit = 0
      } else if (refundCharge) {
        // cost is less than the total credits we have on file and we can refund charges
        // update total cost to be 0 since nothing will be paid without credits
        priceBreakdown.totalCost = 0
        // update remaining credits
        priceBreakdown.remainingCredit = this.round(creditsAndPaid - priceBreakdown.subTotalCost)
      } else if (priceBreakdown.subTotalCost > priceBreakdown.alreadyPaid) {
        // subtotal is more than already paid and no refunds are possible
        let owing = priceBreakdown.subTotalCost - priceBreakdown.alreadyPaid
        // check if the amount owing is more than the user's credits
        if (owing > opts.Account.credits) {
          // work out amount to be paid
          priceBreakdown.totalCost = this.round(owing - opts.Account.credit)
          // remove all credits
          priceBreakdown.remainingCredit = 0
        } else {
          // amount owing is less than total credits
          // nothing to be paid
          priceBreakdown.totalCost = 0
          // only remove the amount owing
          priceBreakdown.remainingCredit = this.round(opts.Account.credits - owing)
        }
      }
      // log how much credit was used (current credits - remaining credit after purchase)
      priceBreakdown.creditUsed = this.round(opts.Account.credits - priceBreakdown.remainingCredit)
    } else {
      // if we're not using credits
      priceBreakdown.totalCost = this.round(priceBreakdown.subTotalCost - priceBreakdown.alreadyPaid)
    }
    // check if total amount to be paid is less than $5 and the user is using credits
    if (priceBreakdown.totalCost <= 5 && priceBreakdown.totalCost > 0 && useCredits) {
      // put the user in negative credit
      priceBreakdown.remainingCredit = -priceBreakdown.totalCost
      // update how many credits are being used
      priceBreakdown.creditUsed += priceBreakdown.totalCost
      // remove the total amount to be paid
      priceBreakdown.totalCost = 0
    }
    // if user is in negative credits
    if (opts.Account.credits < 0) {
      // add it to the total
      priceBreakdown.totalCost += Math.abs(opts.Account.credits)
      // 0 off their credits
      priceBreakdown.remainingCredit = 0
    }
    return { priceBreakdown, opts }
  }

  static calculateDistance (position1, position2) {
    const R = 6378.137
    let lat1 = position1.latitude
    let lat2 = position2.latitude
    let lon1 = position1.longitude
    let lon2 = position2.longitude
    let dLat = (lat2 - lat1) * Math.PI / 180
    let dLon = (lon2 - lon1) * Math.PI / 180
    let a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.sin(dLon / 2) * Math.sin(dLon / 2)
    let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    let distance = R * c * 1000
    return distance
  }

  static parseQuery (form, models) {
    let moment = require('moment-timezone')
    const ZONE = 'Australia/Melbourne'
    let where = {}
    let modelWheres = {}
    let having = false
    form.forEach(
    function ({name, value, type, object, objectType}) {
      // prevent searches with empty strings
      if (typeof value === 'string' && value.replace(/\s/gi, '').length === 0) {
        return false
      }
      if (value || type === 'object' ||
      (type === 'date-range' && value && (value.endDate || value.startDate))) {
        switch (type) {
          case 'search':
            if (name === 'fullName') {
              let names = value.split(' ', 2)
              if (names[1]) {
                where.firstName = { '$iLike': `${names[0]}%` }
                where.lastName = { '$iLike': `${names[1]}%` }
              } else {
                where.$or = {
                  lastName: { '$iLike': `${names[0]}%` },
                  firstName: { '$iLike': `${names[0]}%` }
                }
              }
            } else if (name === 'phone') {
              let query = '('
              for (let c of value) {
                query = `${query}${c}\\s*`
              }
              query += ')'
              where.$or = {
                homePhone: models.sequelize.literal(` "Account"."homePhone" ~ '${query}' `),
                mobilePhone: models.sequelize.literal(` "Account"."mobilePhone" ~ '${query}' `),
                workPhone: models.sequelize.literal(` "Account"."workPhone" ~ '${query}' `)
              }
            } else {
              where[name] = { '$iLike': `${value}%` }
            }
            break

          case 'loose-search':
            where[name] = { '$iLike': `%${value}%` }
            break

          case 'date-range':
            if (value.startDate || value.endDate) {
              where[name] = {}
            }
            if (value.startDate) {
              where[name].$gte = moment(value.startDate).tz(ZONE).format()
            }
            if (value.endDate) {
              where[name].$lte = moment(value.endDate).tz(ZONE).format()
            }
            break

          case 'object':
            if (!modelWheres[object]) modelWheres[object] = {}
            if (value) {
              if (objectType === 'search') {
                if (name === 'fullName') {
                  let names = value.split(' ', 2)
                  if (names[1]) {
                    modelWheres[object].firstName = { '$iLike': `${names[0]}%` }
                    modelWheres[object].lastName = { '$iLike': `${names[1]}%` }
                  } else {
                    modelWheres[object].$or = {
                      lastName: { '$iLike': `${names[0]}%` },
                      firstName: { '$iLike': `${names[0]}%` }
                    }
                  }
                } else {
                  modelWheres[object][name] = {
                    '$iLike': `${value}%`
                  }
                }
              } else if (objectType === 'loose-search') {
                modelWheres[object][name] = { '$iLike': `%${value}%` }
              } else {
                modelWheres[object][name] = value
              }
            }
            break

          case 'relation':
            if (!modelWheres[object]) modelWheres[object] = {}
            if (value) {
              if (objectType === 'search') {
                if (name === 'fullName') {
                  let names = value.split(' ', 2)
                  if (names[1]) {
                    modelWheres[object].firstName = { '$iLike': `${names[0]}%` }
                    modelWheres[object].lastName = { '$iLike': `${names[1]}%` }
                  } else {
                    modelWheres[object].$or = {
                      lastName: { '$iLike': `${names[0]}%` },
                      firstName: { '$iLike': `${names[0]}%` }
                    }
                  }
                } else {
                  modelWheres[object][name] = {
                    '$iLike': `${value}%`
                  }
                }
              } else if (objectType === 'loose-search') {
                modelWheres[object][name] = { '$iLike': `%${value}%` }
              } else {
                modelWheres[object][name] = value
              }
            }
            break

          case 'boolean':
            switch (name) {
              case 'hasReservation':
                if (!(/^false/i).test(value)) {
                  where.ReservationUuid = { $ne: null }
                } else {
                  where.ReservationUuid = null
                }
                break
              case 'baysAvailable':
                if (!(/^false/i).test(value)) {
                  // bays available
                  having = {
                    bays: {
                      $gt: models.sequelize.fn('COUNT', models.sequelize.col('Cars'))
                    }
                  }
                } else {
                  // bays not available
                  having = {
                    bays: {
                      $lte: models.sequelize.fn('COUNT', models.sequelize.col('Cars'))
                    }
                  }
                }
                break
              case 'assigned':
                if (!(/^false/i).test(value)) {
                  where.AccountUuid = { $ne: null }
                } else {
                  where.AccountUuid = null
                }
                break
              case 'deleted':
                if (!(/^false/i).test(value)) {
                  where.deletedAt = { $ne: null }
                } else {
                  where.deletedAt = null
                }
                break
              default:
                where[name] = !(/^false/i).test(value)
                break
            }
            break

          case 'checkboxes':
            modelWheres[object] = value
            break

          case 'number':
            if (name === 'modelReservationId') {
              modelWheres['Reservation'] = {
                reservationId: value
              }
            } else {
              where[name] = value
            }
            break

          default:
            where[name] = value
            break
        }
      }
    })
    return { where, modelWheres, having }
  }

  static serializeForm (form) {
    function setOrPush (target, val) {
      var result = val
      if (target) {
        result = [target]
        result.push(val)
      }
      return result
    }

    function getFormResults (formElement) {
      var formElements = formElement.elements
      var formParams = {}
      var i = 0
      var elem = null
      for (i = 0; i < formElements.length; i++) {
        elem = formElements[i]
        switch (elem.type) {
          case 'submit':
            break
          case 'radio':
            if (elem.checked) {
              formParams[elem.name] = elem.value
            }
            break
          case 'checkbox':
            if (elem.checked) {
              formParams[elem.name] = setOrPush(formParams[elem.name], elem.value)
            }
            break
          default:
            formParams[elem.name] = setOrPush(formParams[elem.name], elem.value)
        }
      }
      return formParams
    }

    return getFormResults(form)
  }

  /**
  *  @desc Finds all accounts that want to be notified of an event
  *  @return Returns the emails of the accounts to be used to send emails
  */
  static async sendNotifications (models, name, messageParams, orgUuid) {
    let bannerAccounts = await models.Account.findAll({
      include: [
        {
          model: models.Organization,
          through: {
            model: models.MemberContract
          },
          as: 'Contracts',
          where: { uuid: orgUuid }
        },
        {
          model: models.Alert,
          where: {
            name,
            type: 'banner'
          }
        }
      ],
      attributes: ['uuid']
    })
    let popUpAccounts = await models.Account.findAll({
      include: [
        {
          model: models.Organization,
          through: {
            model: models.MemberContract
          },
          as: 'Contracts',
          where: { uuid: orgUuid }
        },
        {
          model: models.Alert,
          where: {
            name,
            type: 'popUp'
          }
        }
      ],
      attributes: ['uuid']
    })
    let emailAccounts = await models.Account.findAll({
      include: [
        {
          model: models.Organization,
          through: {
            model: models.MemberContract
          },
          as: 'Contracts',
          where: { uuid: orgUuid }
        },
        {
          model: models.Alert,
          where: {
            name,
            type: 'email'
          }
        }
      ],
      attributes: ['email', 'uuid']
    })
    let emails = emailAccounts.map(function (el, i, arr) {
      return el.email
    })
    if (bannerAccounts.length > 0) {
      messageParams.type = ['banner']
      let bannerMessage = await models.AccountMessage.create(messageParams)
      await bannerMessage.setAccounts(bannerAccounts)
    }
    if (popUpAccounts.length > 0) {
      messageParams.type = ['loginMessage']
      let popUpMessage = await models.AccountMessage.create(messageParams)
      await popUpMessage.setAccounts(popUpAccounts)
    }
    if (emailAccounts.length > 0) {
      messageParams.type = ['email']
      messageParams.message = '<span class="fa fa-paper-plane"></span> Email Notification Was Sent'
      let emailMessage = await models.AccountMessage.create(messageParams)
      await emailMessage.setAccounts(emailAccounts)
    }
    return emails
  }

  /**
  * @desc takes 2 sets of lat/lng and calculates the mid point
  * @returns latitude and longitude for the mid point
  **/
  static middlePoint (lat1, lng1, lat2, lng2) {
    let dLng = (lng2 - lng1) * Math.PI / 180
    lat1 = lat1 * Math.PI / 180
    lat2 = lat2 * Math.PI / 180
    lng1 = lng1 * Math.PI / 180
    let bX = Math.cos(lat2) * Math.cos(dLng)
    let bY = Math.cos(lat2) * Math.sin(dLng)
    let lat3 = Math.atan2(Math.sin(lat1) + Math.sin(lat2), Math.sqrt((Math.cos(lat1) + bX) * (Math.cos(lat1) + bX) + bY * bY))
    let lng3 = lng1 + Math.atan2(bY, Math.cos(lat1) + bX)
    return { lng: lng3 * (180 / Math.PI), lat: lat3 * (180 / Math.PI) }
  }

  /**
  * @desc validates a ReservationPromo object for a period / account
  * @returns the valid ReservationPromo
  * @throws an error description of why the promo could not be applied
  **/
  static validateReservationPromo (options) {
    let moment = require('moment-timezone')
    let defaultOptions = {
      startDate: '',
      endDate: '',
      ReservationPromo: {}
    }
    let opts = { ...defaultOptions, ...options }
    let startDate = moment(opts.startDate)
    let endDate = moment(opts.endDate)
    let bookingHours = moment(opts.endDate).diff(opts.startDate, 'hours')
    let now = moment()
    const DATE_FORMAT = 'h:mma dddd, D MMMM YYYY'
    let timezone = 'Australia/Melbourne'
    if (!opts.ReservationPromo.uuid) throw new Error('No Reservation Promo was passed')
    if (opts.ReservationPromo.expirationType.includes('limitedUse') && opts.ReservationPromo.Reservations.length >= parseInt(opts.ReservationPromo.usageLimit)) {
      throw new Error('Sorry, this promo code is a limited use promo code and has reached maximum uses.')
    }
    if (opts.ReservationPromo.minimumBooking > bookingHours) {
      let minimumBookingEndTime = now.clone().add(opts.ReservationPromo.minimumBooking, 'hours')
      let days = minimumBookingEndTime.diff(now, 'days')
      let hours = minimumBookingEndTime.clone().subtract(days, 'days').diff(now, 'hours', true)
      let errorMessage = 'This promotion requires a minimum booking of'
      if (days > 0) {
        errorMessage += ` ${days} days`
      }
      if (hours > 0) {
        if (days > 0) errorMessage += ' and'
        errorMessage += ` ${hours} hours`
      }
      throw new Error(errorMessage)
    }
    if (opts.ReservationPromo.expirationType.includes('scheduled')) {
      let startWindow = moment(opts.ReservationPromo.startDate)
      let endWindow = moment(opts.ReservationPromo.endDate)
      if (!startDate.isBetween(startWindow, endWindow, null, '[]') && !endDate.isBetween(startWindow, endWindow, null, '[]')) {
        if (opts.ReservationPromo.Cars.length) {
          timezone = opts.ReservationPromo.Cars.pop().Lot.timezone
        }
        throw new Error(`Promotion is only valid between ${startWindow.tz(timezone).format(DATE_FORMAT)} and ${endWindow.tz(timezone).format(DATE_FORMAT)}`)
      }
    }
    return opts.ReservationPromo
  }

  static getExtension (mimetype) {
    switch (mimetype) {
      case 'image/jpeg':
        return 'jpg'
      case 'image/png':
        return 'png'
      case 'image/gif':
        return 'gif'
      case 'application/pdf':
      case 'application/x-pdf':
        return 'pdf'
      default:
        return false
    }
  }
}



// WEBPACK FOOTER //
// ./server/util/index.js